import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Button, Modal, Form, Badge, Spinner, Alert, InputGroup, Stack
} from 'react-bootstrap';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import DataTable from '../../../components/DataTable/DataTable';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';

const UsuariosPage = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRol, setCurrentUserRol] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1
  });

  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [sortField, setSortField] = useState('id_usuario');
  const [sortDirection, setSortDirection] = useState('desc');

  const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });

  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [newPass, setNewPass] = useState('');

  const [showDelete, setShowDelete] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({
    nombre: '', usuario: '', clave: '', correo: '',
    rol: 'proveedor', tipo_persona: 'natural', estado: 'activo'
  });

  const [showEditTipoPersona, setShowEditTipoPersona] = useState(true);

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingNew, setLoadingNew] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingRefresh, setLoadingRefresh] = useState(false);

  const [usuarioExistente, setUsuarioExistente] = useState(false);
  const [usuarioInvalido, setUsuarioInvalido] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    loadUsuarios(currentPage);
  }, [currentPage, itemsPerPage, search, filterTipo, filterEstado, sortField, sortDirection]);

  useEffect(() => {
    if (editData?.rol === 'administrador') {
      setShowEditTipoPersona(false);
    } else {
      setShowEditTipoPersona(true);
    }
  }, [editData?.rol]);

  const loadCurrentUser = async () => {
    try {
      const meRes = await fetch('https://apiprov.conduto.com.ec/api/protected', { credentials: 'include' });
      if (!meRes.ok) throw new Error('No autorizado');
      const me = await meRes.json();
      setCurrentUserId(me.usuario.id_usuario);
      setCurrentUserRol(me.usuario.rol);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadUsuarios = async (page = 1) => {
    setLoading(true);
    setIsSearching(true);
    try {
      const url = new URL('https://apiprov.conduto.com.ec/api/usuarios');
      url.searchParams.append('page', page);
      url.searchParams.append('per_page', itemsPerPage);
      url.searchParams.append('rol', 'proveedor');
      url.searchParams.append('sort_field', sortField);
      url.searchParams.append('sort_direction', sortDirection);

      if (search) url.searchParams.append('search', search);
      if (filterTipo) url.searchParams.append('tipo_persona', filterTipo);
      if (filterEstado) url.searchParams.append('estado', filterEstado);

      const listRes = await fetch(url, { credentials: 'include' });
      if (!listRes.ok) throw new Error('Error al listar');
      const data = await listRes.json();

      setUsuarios(data.usuarios);
      setPagination(data.pagination);
      setCurrentPage(data.pagination.page);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const refreshTable = async () => {
    setLoadingRefresh(true);
    try {
      const url = new URL('https://apiprov.conduto.com.ec/api/usuarios');
      url.searchParams.append('page', currentPage);
      url.searchParams.append('per_page', itemsPerPage);
      url.searchParams.append('rol', 'proveedor');
      url.searchParams.append('sort_field', sortField);
      url.searchParams.append('sort_direction', sortDirection);

      if (search) url.searchParams.append('search', search);
      if (filterTipo) url.searchParams.append('tipo_persona', filterTipo);
      if (filterEstado) url.searchParams.append('estado', filterEstado);

      const listRes = await fetch(url, { credentials: 'include' });
      if (!listRes.ok) throw new Error('Error al actualizar');
      const data = await listRes.json();

      setUsuarios(data.usuarios);
      setPagination(data.pagination);

      showSnackbar('Datos actualizados correctamente', 'success');
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setLoadingRefresh(false);
    }
  };

  const showSnackbar = (text, severity = 'success') => setSnack({ open: true, text, severity });
  const closeSnack = () => setSnack({ ...snack, open: false });

  const generarClave = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let pass = '';
    for (let i = 0; i < 10; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    if (showNew) {
      setNewUser({ ...newUser, clave: pass });
    }
    return pass;
  };

  const getCookie = (name) => {
    const v = `; ${document.cookie}`;
    const p = v.split(`; ${name}=`);
    if (p.length === 2) return p.pop().split(';').shift();
  };

  const registrarUsuario = async () => {
    if (usuarioExistente) {
      showSnackbar('Este nombre de usuario ya está registrado', 'error');
      return;
    }
    if (usuarioInvalido) {
      showSnackbar('El nombre de usuario solo puede contener letras minúsculas y números, sin espacios ni caracteres especiales.', 'error');
      return;
    }
    setLoadingNew(true);
    try {
      const previo = usuarios.find(u => u.estado === 'eliminado' && u.usuario === newUser.usuario);
      const url = previo ? `https://apiprov.conduto.com.ec/api/usuarios/${previo.id_usuario}` : 'https://apiprov.conduto.com.ec/api/register';
      const method = previo ? 'PUT' : 'POST';

      const csrf = getCookie('csrf_access_token');
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf },
        body: JSON.stringify({ ...newUser })
      });
      const data = await res.json();
      if (!res.ok) {
        showSnackbar(data.msg || data.detail, 'error');
        return;
      }

      showSnackbar(previo ? 'Usuario reactivado' : 'Usuario creado con éxito', 'success');
      setShowNew(false);
      setNewUser({
        nombre: '', usuario: '', clave: '', correo: '',
        rol: 'proveedor', tipo_persona: 'natural', estado: 'activo'
      });

      loadUsuarios(1);
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setLoadingNew(false);
    }
  };

  const openDeleteModal = id => { setIdToDelete(id); setShowDelete(true); };
  const closeDeleteModal = _ => { setIdToDelete(null); setShowDelete(false); };

  const handleDelete = async () => {
    if (!idToDelete) return;
    setLoadingDelete(true);
    try {
      const csrf = getCookie('csrf_access_token');
      const res = await fetch(`https://apiprov.conduto.com.ec/api/usuarios/${idToDelete}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRF-TOKEN': csrf }
      });
      const data = await res.json();
      if (!res.ok) {
        showSnackbar(data.msg || data.detail || 'Error', 'error');
        return;
      }

      showSnackbar('Usuario eliminado', 'warning');
      loadUsuarios(currentPage);
      closeDeleteModal();
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setLoadingDelete(false);
    }
  };

  const iniciarEdicion = (u) => {
    setEditData({ ...u });
    setNewPass('');
    setShowEdit(true);
  };

  const guardarEdicion = async () => {
    setLoadingSave(true);
    try {
      const csrf = getCookie('csrf_access_token');
      const payload = { ...editData };
      if (newPass) payload.clave = newPass;
      const res = await fetch(`https://apiprov.conduto.com.ec/api/usuarios/${editData.id_usuario}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        showSnackbar(data.msg || data.detail || 'Error al actualizar', 'error');
        return;
      }
      showSnackbar('Usuario actualizado', 'success');
      setShowEdit(false);
      loadUsuarios(currentPage);
    } catch (err) {
      showSnackbar(err.message, 'error');
    } finally {
      setLoadingSave(false);
    }
  };

  const badgeEstado = (u) => {
    switch (u.estado) {
      case 'activo': return <Badge bg="success">Activo</Badge>;
      case 'inactivo': return <Badge bg="warning">Inactivo</Badge>;
      default: return <Badge bg="light">Desconocido</Badge>;
    }
  };

  const badgeTipo = (u) => {
    switch (u.tipo_persona) {
      case 'natural': return <Badge bg="info">Persona Natural</Badge>;
      case 'juridica': return <Badge bg="primary">Persona Jurídica</Badge>;
      default: return <Badge bg="light">Desconocido</Badge>;
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'tipo_persona') setFilterTipo(value);
    if (key === 'estado') setFilterEstado(value);
  };

  const handleClearFilters = () => {
    setSearch('');
    setFilterTipo('');
    setFilterEstado('');
    setItemsPerPage(10);
    setCurrentPage(1);
    setSortField('id_usuario');
    setSortDirection('desc');
  };

  const handleSortChange = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'id_usuario',
      label: 'N°',
      sortable: false,
      render: (u) => {
        const index = usuarios.findIndex(user => user.id_usuario === u.id_usuario);
        return (currentPage - 1) * itemsPerPage + index + 1;
      }
    },
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: false,
      render: (u) => (
        <div>
          <div className="fw-medium">{u.nombre}</div>
          <small className="text-muted">@{u.usuario}</small>
        </div>
      )
    },
    {
      key: 'correo',
      label: 'Correo',
      sortable: false,
      render: (u) => (
        <small className="text-truncate d-block" style={{ maxWidth: '200px' }}>
          {u.correo}
        </small>
      )
    },
    {
      key: 'tipo_persona',
      label: 'Tipo',
      sortable: false,
      render: (u) => badgeTipo(u)
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: false,
      render: (u) => badgeEstado(u)
    },
    {
      key: 'fecha_creacion',
      label: 'Fecha Creación',
      sortable: true,
      isDate: true,
      render: (u) => u.fecha_creacion ? new Date(u.fecha_creacion).toLocaleString('es-ES') : '—'
    }
  ];

  const filterConfig = [
    {
      key: 'tipo_persona',
      label: 'Todos los tipos',
      options: [
        { value: 'natural', label: 'Persona Natural' },
        { value: 'juridica', label: 'Persona Jurídica' }
      ]
    },
    {
      key: 'estado',
      label: 'Todos los estados',
      options: [
        { value: 'activo', label: 'Activo' },
        { value: 'inactivo', label: 'Inactivo' }
      ]
    }
  ];

  const rowActions = useCallback((usuario) => {
    if (currentUserRol !== 'administrador' && currentUserRol !== 'superusuario') return null;
    return (
      <Stack direction="horizontal" gap={1} className="justify-content-end">
        <Button size="sm" variant="outline-primary" onClick={() => iniciarEdicion(usuario)}>
          Editar
        </Button>
        <Button size="sm" variant="outline-danger" onClick={() => openDeleteModal(usuario.id_usuario)}>
          Eliminar
        </Button>
      </Stack>
    );
  }, [currentUserRol]);

  const mobileCardRenderer = useCallback((usuario) => {
    const index = usuarios.findIndex(u => u.id_usuario === usuario.id_usuario);
    const secuencial = (currentPage - 1) * itemsPerPage + index + 1;

    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="fw-bold text-primary">#{secuencial}</div>
          <div>
            {badgeTipo(usuario)}
            <span className="ms-1">{badgeEstado(usuario)}</span>
          </div>
        </div>
        <div className="mb-2">
          <div className="fw-semibold fs-6">{usuario.nombre}</div>
          <div className="text-muted small">@{usuario.usuario}</div>
        </div>
        <div className="mb-2">
          <div className="text-truncate small">{usuario.correo}</div>
        </div>
      </div>
    );
  }, [usuarios, currentPage, itemsPerPage]);

  const mobileActions = useCallback((usuario) => {
    if (currentUserRol !== 'administrador' && currentUserRol !== 'superusuario') return null;
    return (
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-primary" onClick={() => iniciarEdicion(usuario)} className="flex-fill">
          Editar
        </Button>
        <Button size="sm" variant="outline-danger" onClick={() => openDeleteModal(usuario.id_usuario)} className="flex-fill">
          Eliminar
        </Button>
      </div>
    );
  }, [currentUserRol]);

  if (loading && usuarios.length === 0)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (error) return <Container className="mt-3"><Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert></Container>;

  return (
    <>
      <DataTable
        data={usuarios}
        loading={loading || isSearching}
        error={error}
        totalItems={pagination.total}
        title="Gestión de Proveedores"
        subtitle={`Mostrando ${usuarios.length} de ${pagination.total} proveedores`}
        columns={columns}
        filterConfig={filterConfig}
        rowActions={rowActions}
        mobileCardRenderer={mobileCardRenderer}
        mobileActions={mobileActions}
        headerActions={
          currentUserRol === 'administrador' && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={refreshTable}
                disabled={loadingRefresh}
              >
                {loadingRefresh ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                  </>
                ) : (
                  <>
                    <i className="bi bi-arrow-repeat me-2"></i>
                  </>
                )}
              </Button>
              <Button variant="primary" onClick={() => setShowNew(true)}>
                Agregar proveedor
              </Button>
            </div>
          )
        }
        searchPlaceholder="Buscar por nombre, usuario o correo..."
        searchValue={search}
        onSearchChange={handleSearchChange}
        filterValues={{
          tipo_persona: filterTipo,
          estado: filterEstado
        }}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        itemsPerPageValue={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        currentPageValue={currentPage}
        onPageChange={handlePageChange}
        useServerPagination={true}
        onSortChange={handleSortChange}
        sortField={sortField}
        sortDirection={sortDirection}
        externalPagination={{
          currentPage: currentPage,
          totalPages: pagination.pages,
          onPageChange: handlePageChange,
          onItemsPerPageChange: handleItemsPerPageChange
        }}
        snackbar={snack}
        onCloseSnackbar={closeSnack}
      />

      <Modal show={showNew} onHide={() => setShowNew(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Agregar proveedor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Proveedor</Form.Label>
                <Form.Control
                  value={newUser.nombre}
                  onChange={(e) => setNewUser({ ...newUser, nombre: e.target.value })}
                  placeholder="Nombre del proveedor"
                  maxLength={100}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Usuario</Form.Label>
                <Form.Control
                  value={newUser.usuario}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewUser({ ...newUser, usuario: val });
                    const regex = /^[a-z0-9]*$/;
                    setUsuarioInvalido(!regex.test(val));
                  }}
                  isInvalid={usuarioExistente || usuarioInvalido}
                  placeholder="Nombre de usuario"
                  maxLength={30}
                />
                <Form.Control.Feedback type="invalid">
                  {usuarioExistente
                    ? 'Este nombre de usuario ya está registrado'
                    : 'Solo letras minúsculas y números, sin espacios ni caracteres especiales'}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Correo electrónico</Form.Label>
                <Form.Control
                  type="email"
                  value={newUser.correo}
                  onChange={(e) => setNewUser({ ...newUser, correo: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  maxLength={100}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Tipo de persona</Form.Label>
                <Form.Select
                  value={newUser.tipo_persona}
                  onChange={(e) => setNewUser({ ...newUser, tipo_persona: e.target.value })}
                >
                  <option value="natural">Persona Natural</option>
                  <option value="juridica">Persona Jurídica</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={newUser.estado}
                  onChange={(e) => setNewUser({ ...newUser, estado: e.target.value })}
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Contraseña</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    value={newUser.clave}
                    onChange={(e) => setNewUser({ ...newUser, clave: e.target.value })}
                    placeholder="Genera o escribe una contraseña"
                    maxLength={30}
                  />
                  <Button variant="outline-secondary" onClick={generarClave}>
                    Generar
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNew(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={registrarUsuario}
            disabled={!newUser.nombre || !newUser.usuario || !newUser.correo || !newUser.clave || usuarioExistente || usuarioInvalido || loadingNew}
          >
            {loadingNew ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Agregando...
              </>
            ) : (
              'Agregar proveedor'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="sm" centered>
        <Modal.Header closeButton><Modal.Title>Editar Proveedor</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Nombre</Form.Label>
              <Form.Control value={editData?.nombre || ''} onChange={(e) => setEditData({ ...editData, nombre: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label>Usuario</Form.Label>
              <Form.Control value={editData?.usuario || ''} onChange={(e) => setEditData({ ...editData, usuario: e.target.value })} />
            </Col>
            <Col md={6}>
              <Form.Label>Correo</Form.Label>
              <Form.Control type="email" value={editData?.correo || ''} onChange={(e) => setEditData({ ...editData, correo: e.target.value })} />
            </Col>
            {showEditTipoPersona && (
              <Col md={6}>
                <Form.Label>Tipo Persona</Form.Label>
                <Form.Select value={editData?.tipo_persona || 'natural'} onChange={(e) => setEditData({ ...editData, tipo_persona: e.target.value })}>
                  <option value="natural">Persona Natural</option>
                  <option value="juridica">Persona Jurídica</option>
                </Form.Select>
              </Col>
            )}
            <Col md={6}>
              <Form.Label>Estado</Form.Label>
              <Form.Select value={editData?.estado || 'activo'} onChange={(e) => setEditData({ ...editData, estado: e.target.value })}>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Nueva Contraseña (dejar vacío para no cambiar)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="password"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  autoComplete="new-password"
                />
                <Button variant="outline-primary" onClick={() => setNewPass(generarClave())}>Generar</Button>
              </InputGroup>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardarEdicion} disabled={loadingSave}>
            {loadingSave ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showDelete}
        onHide={closeDeleteModal}
        onConfirm={handleDelete}
        title="¿Está seguro que desea eliminar este proveedor?"
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmVariant="danger"
        loading={loadingDelete}
        icon={<i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>}
      />
    </>
  );
};

export default UsuariosPage;